import React, { Component } from 'react';
import { withRouter } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import { parseSegmentosProfundos, resolverContextoProfundo, codificarContexto, PARAM_CONTEXTO } from 'hay-core-react';
/**
 * RotaProfundaGuard — interpreta os segmentos extras de uma URL profunda (deep link) antes de
 * montar o componente real da rota, e faz o `navigate` necessário pra deixar o grid (próprio ou
 * relacionado) no estado esperado — tudo convergindo pro mesmo mecanismo de `?id=&acao=` que
 * `EntityGrid` já sabe interpretar (ver `EntityGrid.abrirRegistroPorId`).
 *
 * Duas formas suportadas, resolvidas dinamicamente a partir do conteúdo dos segmentos (não só
 * da posição — ver `parseSegmentosProfundos`):
 *   - `/entidade/id/acao`                       -> ação na própria entidade
 *   - `/entidade/idPai/relacaoChave/idFilho/acao?` -> navegação pra relação filha
 *
 * Sem segmentos extras (ou com uma relação desconhecida/typo), renderiza o componente da rota
 * normalmente — comportamento de hoje 100% preservado.
 */
class RotaProfundaGuard extends Component {
    constructor(props) {
        super(props);
        this.state = { resolvendo: true };
    }

    componentDidMount() {
        this.resolver();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params?.['*'] !== this.props.params?.['*']) {
            this.setState({ resolvendo: true }, () => this.resolver());
        }
    }

    async resolver() {
        const { cfg, controllerKeyAtual, rotaRegistry, navigate, searchparams, params, getRouter } = this.props;
        const splat = params?.['*'];
        const { proprio, relacao } = parseSegmentosProfundos(splat, rotaRegistry, controllerKeyAtual);

        if (!proprio && !relacao) {
            this.setState({ resolvendo: false });
            return;
        }

        UtilService.showLoading();
        try {
            // O `navigate()` do react-router só funciona depois que o próprio efeito de layout
            // do componente que o gerou (`withRouter`) roda — antes disso, ele descarta a
            // chamada silenciosamente (só um warning no console), porque o efeito ainda não
            // marcou a referência interna como pronta. Como o componentDidMount de uma classe
            // filha roda ANTES do useLayoutEffect do wrapper funcional pai (withRouter), chamar
            // navigate() aqui de forma síncrona (sem nenhum await antes) perde essa corrida no
            // primeiro mount — a URL de deep link nunca é resolvida e a tela fica em branco pra
            // sempre (resolvendo nunca vira false). Um único await (mesmo trivial) é suficiente
            // pra garantir que o efeito já rodou antes de qualquer navigate() abaixo.
            await Promise.resolve();

            if (proprio) {
                navigate({ pathname: cfg.rota, search: { id: proprio.id, acao: proprio.acao } }, { replace: true });
                return;
            }

            const getFetcher = (key) => rotaRegistry[key]?.entidade;
            const contexto = await resolverContextoProfundo({
                rotaRegistry,
                controllerKeyAtual,
                idPai:        relacao.idPai,
                relacaoChave: relacao.relacaoChave,
                idFilho:      relacao.idFilho,
                acao:         relacao.acao,
                searchParams: searchparams,
                getFetcher,
            });

            if (!contexto) {
                this.setState({ resolvendo: false }); // relação desconhecida -> grid normal
                return;
            }

            const ctxCodificado = codificarContexto(contexto.ctx);
            const searchBase = ctxCodificado ? { [PARAM_CONTEXTO]: ctxCodificado } : {};

            if (!contexto.idFilho) {
                // Só idPai/relacaoChave na URL (sem registro filho) -> um único waypoint, a
                // lista já filtrada pelo pai. Comportamento de histórico inalterado.
                navigate({ pathname: contexto.relCfg.rota, search: searchBase }, { replace: true });
                return;
            }

            // Com registro filho na URL: dois waypoints de histórico, pra "voltar" funcionar
            // em etapas (form -> lista filtrada -> o que existia antes do deep link).
            //
            // Waypoint 1 (lista filtrada) reescreve a entrada atual — a do deep link, que nunca
            // chegou a renderizar nada. Usa `router.navigate()` (API imperativa de baixo nível
            // do react-router, via `getRouter()` — ver App.js) em vez de `window.history` bruto:
            // mexer direto no histórico do browser desalinha o rastreamento interno que o
            // react-router mantém (índice/posição), e o próximo "voltar" do usuário para de
            // disparar re-render (a URL muda mas o React nunca fica sabendo). `router.navigate`
            // devolve uma Promise de verdade, então dá pra aguardar antes do segundo waypoint
            // sem risco das duas chamadas colidirem em uma única entrada de histórico.
            const queryBase = new URLSearchParams(searchBase).toString();
            const router = getRouter?.();
            if (router) {
                await router.navigate(
                    { pathname: contexto.relCfg.rota, search: queryBase },
                    { replace: true }
                );
            }

            // Waypoint 2 (registro aberto): push normal do react-router.
            navigate(
                { pathname: contexto.relCfg.rota, search: { ...searchBase, id: contexto.idFilho, acao: contexto.acaoAlvo } },
                { replace: false }
            );
        } finally {
            UtilService.hideLoading();
        }
    }

    render() {
        if (this.state.resolvendo) return null;
        const { cfg, permissoes } = this.props;
        const Componente = cfg.componente;
        return <Componente permissoes={permissoes} />;
    }
}

export default withRouter(RotaProfundaGuard);
